import { useState } from "react";
import { useToast } from "./use-toast";
import { Invoice, ACHRecord } from "@/types";
import { supabase } from "@/integrations/supabase/client";

/**
 * Hook to manage ACH file generation
 */
export function useACH(invoicesForACH: Invoice[], refetchInvoices: () => void) {
  const [isGeneratingACH, setIsGeneratingACH] = useState(false);
  const [achHistory, setAchHistory] = useState<ACHRecord[]>([]);
  const { toast } = useToast();

  const generateACHFile = async () => {
    if (invoicesForACH.length === 0) {
      toast({
        title: "No invoices ready",
        description: "No early payment invoices ready for ACH.",
        variant: "destructive",
      });
      return;
    }

    setIsGeneratingACH(true);
    try {
      const lines: string[] = [];
      const today = new Date();
      const dateStr = today.toISOString().split("T")[0].replace(/-/g, "");

      lines.push(`101 DISCORUN AI PAYMENTS ${dateStr}`);
      lines.push(`5200 DISCORUN AI EARLY PAYMENT BATCH ${dateStr}`);

      invoicesForACH.forEach((inv, idx) => {
        const amount = (inv.discounted_payment_amount || inv.payment_amount)
          .toFixed(2)
          .replace(".", "");
        lines.push(
          `6220${String(idx + 1).padStart(10, "0")}${amount.padStart(12, "0")} ${inv.supplier_name.substring(0, 22).padEnd(22)} ${inv.supplier_email}`
        );
      });

      const totalAmount = invoicesForACH.reduce(
        (sum, inv) => sum + (inv.discounted_payment_amount || inv.payment_amount),
        0
      );
      const totalStr = totalAmount.toFixed(2).replace(".", "");
      lines.push(
        `8200${String(invoicesForACH.length).padStart(6, "0")}${totalStr.padStart(12, "0")}`
      );
      lines.push(`9000001000001${totalStr.padStart(12, "0")}`);

      const achContent = lines.join("\n");
      const fileName = `discorun_ach_${today.toISOString().split("T")[0]}.txt`;

      // Save to history
      const record: ACHRecord = {
        id: Date.now().toString(),
        date: today,
        invoiceCount: invoicesForACH.length,
        totalAmount,
        fileName,
        content: achContent,
      };
      setAchHistory((prev) => [record, ...prev]);

      // Download
      const blob = new Blob([achContent], { type: "text/plain" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      const invoiceIds = invoicesForACH.map((inv) => inv.id);
      const { error } = await supabase
        .from("invoices")
        .update({ status: "paid" as any })
        .in("id", invoiceIds);
      if (error) throw error;

      toast({
        title: "ACH file generated",
        description: `${invoicesForACH.length} invoice(s) marked as completed.`,
      });
      refetchInvoices();
    } catch (error) {
      console.error("Error generating ACH file:", error);
      toast({
        title: "Error",
        description: "Failed to generate ACH file.",
        variant: "destructive",
      });
    } finally {
      setIsGeneratingACH(false);
    }
  };

  return {
    isGeneratingACH,
    achHistory,
    generateACHFile,
  };
}
